import { useEffect, useMemo, useState } from "react";
import { Wrench, Plus, Trash2, Check, Edit3 } from "lucide-react";
import {
  Page, Panel, SearchInput, usePagination, Pagination, DeleteButton,
  Modal, ModalActions, Field, SelectField, TextArea,
} from "../lib/ui";
import { api, humanDate, today } from "../lib/api";
import { CustomerCombobox } from "./Modals";

const STATUSES = ["Kabul", "Tamirde", "Hazır", "Teslim"];
const STATUS_TONE = { Kabul: "pending", Tamirde: "info", "Hazır": "paid", Teslim: "muted" };

function TicketModal({ customers, initial, onClose, onSaved, onCustomerAdded }) {
  const [form, setForm] = useState({
    customer_id: initial?.customer_id || null,
    customer_name: initial?.customer_name || "",
    device: initial?.device || "",
    plate: initial?.plate || "",
    chassis_no: initial?.chassis_no || "",
    fault: initial?.fault || "",
    received_date: initial?.received_date || today(),
    delivered_date: initial?.delivered_date || "",
    status: initial?.status || "Kabul",
    note: initial?.note || "",
  });
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (initial?.id) await api.put(`/service-tickets/${initial.id}`, form);
      else await api.post("/service-tickets", form);
      onSaved();
    } catch (ex) { setErr(ex.response?.data?.detail || "Kaydedilemedi"); }
  };

  return (
    <Modal title={initial?.id ? "Servis Kaydını Düzenle" : "Yeni Servis Kaydı"}
           subtitle="Servise alınan cihazı adım adım takip edin."
           onClose={onClose} size="lg" testid="service-modal">
      <form onSubmit={submit} data-testid="service-form">
        <div className="row">
          <div style={{ flex: 1 }}>
            <label className="field-label">Müşteri</label>
            <CustomerCombobox customers={customers} selectedId={form.customer_id} selectedName={form.customer_name}
                              onSelect={(c) => setForm({ ...form, customer_id: c?.id || null, customer_name: c?.name || "" })}
                              onCreateNew={onCustomerAdded} />
          </div>
          <SelectField label="Durum" testid="service-status" value={form.status}
                       onChange={(e) => setForm({ ...form, status: e.target.value })}>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </SelectField>
        </div>
        <div className="row">
          <Field label="Cihaz / Marka Model" testid="service-device" value={form.device}
                 onChange={(e) => setForm({ ...form, device: e.target.value })} required />
          <Field label="Plaka (opsiyonel)" testid="service-plate" value={form.plate}
                 onChange={(e) => setForm({ ...form, plate: e.target.value })} />
        </div>
        <div className="row">
          <Field label="Şase No (opsiyonel)" testid="service-chassis" value={form.chassis_no}
                 onChange={(e) => setForm({ ...form, chassis_no: e.target.value })} />
          <Field label="Gelen Tarih" testid="service-received" type="date" value={form.received_date}
                 onChange={(e) => setForm({ ...form, received_date: e.target.value })} />
          <Field label="Teslim Tarihi" testid="service-delivered" type="date" value={form.delivered_date}
                 onChange={(e) => setForm({ ...form, delivered_date: e.target.value })} />
        </div>
        <TextArea label="Arıza" testid="service-fault" value={form.fault}
                  onChange={(e) => setForm({ ...form, fault: e.target.value })} rows={2} />
        <TextArea label="Not" testid="service-note" value={form.note}
                  onChange={(e) => setForm({ ...form, note: e.target.value })} rows={2} />
        {err && <div className="error-text">{err}</div>}
        <ModalActions onCancel={onClose} submitLabel="Kaydet" submitTestId="service-submit" />
      </form>
    </Modal>
  );
}

export default function Service({ user, customers = [], onCustomerAdded }) {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [modal, setModal] = useState(null);

  const load = async () => {
    const { data } = await api.get("/service-tickets");
    setRows(data);
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => rows.filter((r) => {
    if (status !== "all" && r.status !== status) return false;
    if (!q) return true;
    const n = q.toLowerCase();
    return [r.customer_name, r.device, r.plate, r.chassis_no, r.fault]
      .some((v) => (v || "").toLowerCase().includes(n));
  }), [rows, status, q]);
  const pg = usePagination(filtered);
  const counts = STATUSES.reduce((o, s) => ({ ...o, [s]: rows.filter((r) => r.status === s).length }), {});

  const del = async (id) => {
    if (!window.confirm("Servis kaydı silinsin mi?")) return;
    await api.delete(`/service-tickets/${id}`);
    load();
  };
  const quickStatus = async (t, next) => {
    await api.put(`/service-tickets/${t.id}`, { ...t, status: next });
    load();
  };

  return (
    <>
      <Page eyebrow="MUHASEBE" title="Servis Takibi"
            subtitle={`Servise alınan cihazların durum akışı. (${counts["Kabul"] + counts["Tamirde"] + counts["Hazır"] || 0} aktif)`}
            action={<button className="primary-button" data-testid="service-add-btn"
                            onClick={() => setModal({})}>
              <Plus size={14} /> Yeni Servis Kaydı
            </button>} />
      <Panel className="module-panel"
             action={<SearchInput value={q} onChange={setQ} testid="service-search"
                                  placeholder="Müşteri, cihaz, plaka veya arıza ara" />}>
        <div className="tab-row" style={{ marginBottom: 10 }}>
          {[["all", `Tümü (${rows.length})`], ...STATUSES.map((s) => [s, `${s} (${counts[s] || 0})`])].map(([k, l]) => (
            <button key={k} data-testid={`service-tab-${k}`}
                    className={status === k ? "tab active" : "tab"}
                    onClick={() => setStatus(k)}>{l}</button>
          ))}
        </div>
        <div className="table-wrap">
          <table>
            <thead>
              <tr><th>Tarih</th><th>Müşteri</th><th>Cihaz</th><th>Plaka</th><th>Arıza</th><th>Durum</th><th></th></tr>
            </thead>
            <tbody>
              {pg.slice.length === 0 && <tr><td colSpan="7" className="empty">Servis kaydı yok.</td></tr>}
              {pg.slice.map((t) => (
                <tr key={t.id} data-testid={`service-row-${t.id}`}>
                  <td>{humanDate(t.received_date || t.created_at)}</td>
                  <td><strong>{t.customer_name || "—"}</strong></td>
                  <td><Wrench size={12} /> {t.device || "—"}</td>
                  <td>{t.plate || "—"}</td>
                  <td className="truncate" title={t.fault}>{t.fault || "—"}</td>
                  <td><span className={`status-pill ${STATUS_TONE[t.status] || "pending"}`}>{t.status}</span></td>
                  <td className="row-actions">
                    {t.status !== "Teslim" && (
                      <button className="icon-button" title="Durumu ilerlet"
                              data-testid={`service-advance-${t.id}`}
                              onClick={() => {
                                const idx = STATUSES.indexOf(t.status);
                                quickStatus(t, STATUSES[Math.min(idx + 1, STATUSES.length - 1)]);
                              }}>
                        <Check size={14} />
                      </button>
                    )}
                    <button className="icon-button" title="Düzenle"
                            data-testid={`service-edit-${t.id}`}
                            onClick={() => setModal(t)}>
                      <Edit3 size={14} />
                    </button>
                    {user.role === "admin" && (
                      <DeleteButton testid={`service-delete-${t.id}`} onClick={() => del(t.id)} />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination page={pg.page} pageCount={pg.pageCount} total={pg.total}
                      onChange={pg.setPage} testid="service-pagination" />
        </div>
      </Panel>
      {modal && (
        <TicketModal customers={customers} initial={modal.id ? modal : null}
                     onClose={() => setModal(null)}
                     onSaved={() => { setModal(null); load(); }}
                     onCustomerAdded={onCustomerAdded} />
      )}
    </>
  );
}
